import { getAllMaterials, updateMaterialInDb } from '@sp-firebase/materialsFB';
import { toast } from 'react-toastify';
import { getAccount } from '../../sp-firebase/accountFB';
import { deleteBalancesOfAccount } from '../../sp-firebase/balanceFlowFB';
import { deleteFutureOrders } from '../../sp-firebase/futureOrderFB';
import { adminGetItems, saveItem } from '../../sp-firebase/genericFB';
import { getAccountInventory, updateAccountInventory } from '../../sp-firebase/inventoryFB';
import { deleteInventoryReports } from '../../sp-firebase/inventoryReportFB';
import { getAllProductsByAccountId } from '../../sp-firebase/productFB';
import { createAllDocs, getAllDocs } from '../../sp-firebase/routesFB';
import { IRoute } from '../../sp-types';
import { IFutureOrder } from '../../sp-types/FutureOrderType';
import { IInventory } from '../../sp-types/InventoryType';
import { DashboardNotification } from '@sp-types/notificationType';
import { Account, Company, Contact, IFormulaToAccount, IRiskPercent } from '../../sp-types/accountTypes';
import { Product } from '../../sp-types/productType';
import { SpotLockUser } from '../../sp-types/userType';
import { createEmptyRiskTolerance } from '../../utils/accountUtils';
import { IInstrument, refinitivMonthCodes } from '../../sp-types/instrumentType';
import { deletePositions } from '../../sp-firebase/financialPositionsFB';
import { clearExposure } from '../../sp-firebase/exposureFB';
import { Timestamp } from 'firebase/firestore';
import { createInstrument, getInstrumentsByIds } from '../../sp-firebase/instrumentFB';

/* eslint-disable @typescript-eslint/no-unused-vars */

export interface BatchOrder {
  orderId: string;
  formulaId: string;
  fromDate: string;
  quantity: number;
  monthSpread: number;
  riskDanger?: number;
  riskWarn?: number;
  riskGood?: number;
}

export const emptyBatch: BatchOrder = {
  orderId: 'order2',
  formulaId: 'HRCc1',
  fromDate: new Date().toLocaleDateString(),
  quantity: 14_000_000,
  monthSpread: 12,
  riskDanger: 60_000000,
  riskWarn: 5_000000,
  riskGood: 1_000_000,
};

const supplierHubUsers = {
  TvcsAN2f67OaAopJUXQYvt9Ib062: 'micha@spot',
  YYm8p02D3SVqFVkwiJrDPwfkYqw2: 'roy@spot',
  MvmzAt9DX6Q4Co2vfAH09iAMx7m2: 'yossi@spot',
  aJze6P1TpsRssJsUhYyYbn74K7l1: 'moshe@spot',
  AmkmiKxrWUNs0lHy7gxHdsfHqhS2: 'matanel@spo',
  FGcR5HE9t2Y6pKnU56JamDGfCNf2: 'matanel@gmail',
  CmF6YcaJhzQm2bvS2wwO1XqtUIv1: 'relishkov@gmai',
  ecDUvwow8GfgI63sGBhxqLyquQR2: 'udi.nahum@gmai',
};

const spotlockDevUsers = {
  yYzrd2Y2bjdauE97UMmPZUfR0Gw1: 'moshe@spot',
  bh7F0vdHRdNBzB2mk1yVBJIdCCv1: 'yossi@spot',
  dOpVRv18BzYgnbcZpxOGymjeBnn1: 'roy@spot',
  g5JksVzJotgB3vaK9sab6EPp2a33: 'roy2@spot',
  '8Qyxp6cUsYa7vsqL4zB0RSPc5Hu2': 'natan@spot',
  Ur1t9JT3jqa1paTGCvyUspB791B2: 'matanel',
};
const spotlockProdUsers = {
  szZwp4QfHoSdqGVEo6VEcWhJBMm1: 'moshe-spo',
  YfT4rpP07LgwcIzwi6rIYaInNn53: 'Roy@sp',
  tTKE1gF4jYWS5IxF4kqKoKqIsSs1: 'micha@sp',
  RHfwvFNdN6MKGatHfTwBn2C5Dju2: 'yossi@sp',
  CS2ymxbpYqg3kC2X0t3AZmZo5Kb2: 'matanel@sp',
};

export const getEmptyAccountMaterials = (
  accountId: string,
  userData: SpotLockUser,
  batch: BatchOrder,
  formulasToAccount: IFormulaToAccount[],
): Account => ({ ...getEmptyAccount(accountId, userData, batch), formulasToAccount });

export const mapFormulaToAccount = (
  materials: string[],
  { riskDanger, riskWarn, riskGood }: BatchOrder,
): IFormulaToAccount[] =>
  materials.map((formulaId) => ({
    formulaId,
    RiskTolerance: createEmptyRiskTolerance(riskDanger, riskWarn, riskGood),
    RoutePriority: [],
  }));

export const getEmptyAccount = (
  accountId: string,
  userData: SpotLockUser,
  { riskDanger, riskWarn, riskGood }: BatchOrder,
  formulaToAccount?: IFormulaToAccount[],
  riskPercents?: IRiskPercent,
): Account => ({
  creator: userData.id,
  country: userData.country,
  companyDetails: {} as Company,
  contactDetails: {} as Contact, // contactDetails,
  status: 'active',
  collateralDetails: {
    allocatedCollateral: 0,
    availableCollateral: 0,
    newCollateralAmount: 0,
    confirmationDoc: [],
    immediateConfirmation: 0,
    marginCall: 0,
  },
  AdjustmentsInfo: {
    materialAdjustmentsOptions: ['Last qtr avg.', 'Trailing month', 'Spot'],
    productAdjustmentOptions: ['Last qtr avg.', 'Trailing month', 'Spot'],
    productPriceIndexes: ['interest', 'Consumer price index', 'HRC index'],
  },
  formulasToAccount: formulaToAccount || [
    {
      formulaId: 'general',
      RiskTolerance: createEmptyRiskTolerance(riskDanger, riskWarn, riskGood),
      RoutePriority: [],
    },
    {
      formulaId: 'CSSCc1',
      RiskTolerance: createEmptyRiskTolerance(riskDanger, riskWarn, riskGood),
      RoutePriority: [
        { order: 0, routeId: 'route19' },
        { order: 1, routeId: 'route20' },
        { order: 2, routeId: 'route21' },
      ],
    },
    {
      formulaId: 'HRCc1',
      RiskTolerance: createEmptyRiskTolerance(riskDanger, riskWarn, riskGood),
      RoutePriority: [
        { routeId: 'route22', order: 0 },
        { order: 1, routeId: 'route23' },
        { routeId: 'route24', order: 2 },
      ],
    },
    {
      formulaId: 'CRCc1',
      RiskTolerance: createEmptyRiskTolerance(riskDanger, riskWarn, riskGood),
      RoutePriority: [
        { routeId: 'route22', order: 0 },
        { order: 1, routeId: 'route23' },
        { routeId: 'route24', order: 2 },
      ],
    },
    {
      formulaId: 'CSRRc1',
      RiskTolerance: createEmptyRiskTolerance(riskDanger, riskWarn, riskGood),
      RoutePriority: [
        { routeId: 'route16', order: 0 },
        { routeId: 'route17', order: 1 },
        { routeId: 'route18', order: 2 },
      ],
    },
    {
      formulaId: 'BUSc1',
      RiskTolerance: createEmptyRiskTolerance(riskDanger, riskWarn, riskGood),
      RoutePriority: [
        { order: 0, routeId: 'route1' },
        { order: 1, routeId: 'route2' },
        { order: 2, routeId: 'route3' },
      ],
    },

    {
      formulaId: 'CMCU3',
      RiskTolerance: createEmptyRiskTolerance(riskDanger, riskWarn, riskGood),
      RoutePriority: [
        { order: 0, routeId: 'route7' },
        { routeId: 'route8', order: 1 },
        { routeId: 'route9', order: 2 },
      ],
    },

    {
      formulaId: 'CMZN3',
      RiskTolerance: createEmptyRiskTolerance(riskDanger, riskWarn, riskGood),
      RoutePriority: [
        { order: 0, routeId: 'route10' },
        { routeId: 'route11', order: 1 },
        { order: 2, routeId: 'route12' },
      ],
    },
    {
      formulaId: 'CMAL3',
      RiskTolerance: createEmptyRiskTolerance(riskDanger, riskWarn, riskGood),
      RoutePriority: [
        { order: 0, routeId: 'route4' },
        { order: 1, routeId: 'route5' },
        { order: 2, routeId: 'route6' },
      ],
    },
  ],
  users: {
    [userData.id]: 'user@email.com',
    ...((window as any).isProd ? supplierHubUsers : (window as any).isStaging ? spotlockProdUsers : spotlockDevUsers),
  },
  riskPercents: riskPercents || {
    volatility: '73%',
    riskBackTesting: [
      { key: '5%', value: 55 },
      { key: '10%', value: 37 },
      { key: '25%', value: 21 },
    ],
  },
});

export const addNotification = async (accountId: string, notification: DashboardNotification) => {
  const accInventory = await getAccountInventory(accountId);
  if (accInventory) {
    accInventory.notifications.push(notification);

    await updateAccountInventory(accountId, accInventory);
  }
};

export const createEmptyInventory = (accountId: string): IInventory => ({
  materialInventory: [
    {
      formulaId: 'general',
      materialName: 'General',
      exposureRisk: 0,
      futureOrders: [],
      inventoryProducts: [],
      financialPositions: [],
    },
    {
      formulaId: 'HRCc1',
      materialName: 'Steel HRC',
      exposureRisk: 0,
      futureOrders: [],
      inventoryProducts: [],
      financialPositions: [],
    },
    {
      formulaId: 'CMAL3',
      materialName: 'LME Aluminium',
      exposureRisk: 0,
      futureOrders: [],
      inventoryProducts: [],
      financialPositions: [],
    },
    {
      formulaId: 'CMCU3',
      materialName: 'LME Copper',
      exposureRisk: 0,
      futureOrders: [],
      inventoryProducts: [],
      financialPositions: [],
    },
    {
      formulaId: 'CMZN3',
      materialName: 'LME Zinc',
      exposureRisk: 0,
      futureOrders: [],
      inventoryProducts: [],
      financialPositions: [],
    },
    {
      formulaId: 'CRCc1',
      materialName: 'Steel HRC',
      exposureRisk: 0,
      futureOrders: [],
      inventoryProducts: [],
      financialPositions: [],
    },
  ],
  accountId,
  notifications: [],
  statusFiles: [],
});

export const generateFutureOrder = ({
  formulaId,
  orderId,
  quantity,
  fromDate,
  monthSpread,
}: BatchOrder): IFutureOrder => {
  const startingDate = new Date(fromDate);

  const months = Array.from({ length: monthSpread }).map((_, i) => ({
    year: startingDate.getFullYear() + Math.floor((startingDate.getMonth() + i) / 12),
    month: ((startingDate.getMonth() + i) % 12) + 1,
  }));
  const lastDelivery = months[monthSpread - 1];
  return {
    updatedAt: Timestamp.now(),
    orderId,
    status: 'completed',
    productId: '1',
    fileUrl: [],
    supplierExposure: [
      {
        formulaId,
        weightInProduct: 100,
        basePrice: 550,
        supplyLeadTime: 0,
        adjustment: '',
        exposureCalendar: months.map((date) => ({ value: quantity / monthSpread, date })),
      },
    ],
    orderDetails: {
      deliveryType: 'Estimate',
      quantity,
      startingDate: fromDate,
      deliveryDates: [{ month: startingDate.getMonth() + 1, year: startingDate.getFullYear() }, lastDelivery],
      productDeliveryCalendar: months.map((monthYear) => ({ value: quantity / monthSpread, date: monthYear })),
    },
    clientExposure: {
      [formulaId]: {
        indexOffset: 0,
        adjustment: '',
        basePrice: 550,
        priceIndex: 'Trailing month',
        weightInProduct: 100,
        exposureCalendar: months.map((date) => ({ value: quantity / monthSpread, date })),
      },
    },
    totalExposure: [
      {
        formulaId,
        exposureCalendar: months.map((date) => ({ value: quantity / monthSpread, date })),
      },
    ],
  } as IFutureOrder;
};

export const restartAccount = async (account: Account, userData: SpotLockUser) => {
  await ClearAccount(account.id || '', userData, emptyBatch, account);
};
export const ClearAccount = async (accountId: string, userData: SpotLockUser, batch: BatchOrder, account?: Account) => {
  try {
    //const accountData = getEmptyAccount(accountId, userData, batch, account?.formulasToAccount, account?.riskPercents);

    await Promise.all([
      deleteFutureOrders(accountId),
      deleteInventoryReports(accountId),
      deletePositions(accountId),
      updateAccountInventory(accountId, createEmptyInventory(accountId)),
      deleteBalancesOfAccount(accountId),
      clearExposure(
        accountId,
        account?.formulasToAccount?.map((a) => a.formulaId),
      ),
      //updateAccount(accountId, accountData),
    ]);
  } catch (e) {
    console.error('error while clearing account', e);
  }
};

export const ClearAccountWHSingleMaterial = async (account: Account, userData: SpotLockUser) => {
  await restartAccount(account, userData);
  // await addInventory(accountId, 'HRCc1', 0);
};

//ONLY for new database start
const materialNames: Record<string, string> = {
  HRCc1: 'HR Steel',
  BUSc1: 'Busheling Scrap',
  CMAL3: 'Aluminium',
  CMCU3: 'Copper',
  CMZN3: 'Zinc',
  'CNY=': 'Chinease Yuan to USD convertion',
  CRCc1: 'CR Steel',
  CSRRc1: 'Rebar',
  CSSCc1: 'HMS Scrap',
  SCFc1: '[SCFc1] (SHFE Copper)',
  SRBc1: 'SHFE Rebar',
};

// const oneTimeDataLoad = async () => {
//const balanceInstructions = await getAllDocs('balance-instructions');
// const baseInstr = await getAllBaseInstruments();
//const instr = await getAllInstruments();
//console.log('routes', routes);
// await createAllRoutes(routes);
// await createAllDocs(baseInstruments, 'base-instruments');
// await createAllDocs(
//   instruments.map((i) => ({ ...i, expiryDate: new Timestamp(i.expiryDate, 0) })),
//   'instruments',
// );
//await createAllDocs(balanceInstructions, 'balance-instructions');
// console.log('baseInstr', baseInstr);
// console.log('instr', instr);
// };

const fixRoutes = async () => {
  const currRoutes = await getAllDocs('routes');

  const orderedRoutes = currRoutes.map((r: IRoute, order: number) => ({ ...r, order }));

  await createAllDocs(orderedRoutes, 'routes');

  toast.info(`updated ${orderedRoutes.length} routes`);
};

const toMap = function <T extends { id: string }>(products: T[]) {
  return products.reduce((result, pro) => ({ ...result, [pro.id]: pro }), {} as Record<string, T>);
};
export const fixProducts = async (accountId: string) => {
  //@ts-expect-error type problem
  const [account] = await getAccount(accountId);

  const products: Product[] = account.products;
  const oldProducts = toMap<Product>(products);

  const existingProds = await getAllProductsByAccountId(accountId);
  const existingMap = toMap<Product>(existingProds || []);

  const createProducts = { ...(existingMap || {}), ...(oldProducts || {}) };
  await saveItem<any>(accountId, createProducts, 'product');

  toast.info(`created ${Object.keys(createProducts).length} products`);
};

export const fixMaterialNames = async () => {
  // await oneTimeDataLoad();
  await fixRoutes();

  const materials = await getAllMaterials();

  const promises = materials
    ?.filter(({ name }) => !name || (name.includes('<') && name.includes('>')))
    .map((material) => updateMaterialInDb({ ...material, name: materialNames[material.id] || material.name }));

  await Promise.all([promises]);

  toast.info('Fixed materials: ' + promises?.length);
};

export const copyInstruments = async (fromInstr: string, toInstr: string): Promise<number> => {
  const allInstruments = await adminGetItems<IInstrument>('instruments');

  const createInstruments = allInstruments
    ?.filter((a) => a.baseInstrumentId === fromInstr)
    .map(({ id, ...rest }) => ({
      ...rest,
      baseInstrumentId: toInstr,
      id: id.replace(fromInstr, toInstr),
    }));

  if (createInstruments && createInstruments.length > 0) {
    const results = await Promise.all(
      // eslint-disable-next-line no-unused-vars
      createInstruments.map(({ firebaseId, ...instrument }: any) => {
        return saveItem(instrument.id, instrument, 'instruments') || Promise.resolve([]);
      }),
    );

    return results.length;
  }
  return 0;
};

export const genInstruments = async (original: string, fromDate: string, months: string) => {
  const instruments = await getInstrumentsByIds([original]);

  const orig = instruments?.[0];

  if (!orig) {
    console.warn('orrig Intrument not found');
    return;
  }

  const dateStart = new Date(fromDate);
  const insToCreate = Array.from({ length: +months }).map((_, i) => ({
    ...orig,
    expiryDate: Timestamp.fromDate(new Date(dateStart.getFullYear(), dateStart.getMonth() + i, 27)),
    id:
      orig.baseInstrumentId +
      refinitivMonthCodes[(dateStart.getMonth() + i) % 12] +
      dateStart.getFullYear().toString().replace('20', ''),
  }));

  await Promise.all(insToCreate.map((i) => createInstrument(i, i.id)));

  console.debug('created ', insToCreate.length, orig, fromDate, months);
};

// const addCategories = async () => {
//   mockProductCategories.forEach(async (element) => {
//     await createProductCategoryInFb(element);
//   });
// };

// // @ts-expect-error ts(6133)
// const addInventory = async (accountId: string, formulaId: string, quantity: number) => {
//   const reportDate = new Date();
//   reportDate.setMonth(0);

//   const inventoryReport = {
//     accountId,
//     formulaId,
//     quantity,
//     reportingTime: Timestamp.fromDate(reportDate),
//     monthYear: { month: reportDate.getMonth(), year: reportDate.getFullYear() } as MonthYear,
//   } as IInventoryReportType;
//   await createInventoryReportTypeInDb(inventoryReport);
// };
